import React, { useEffect, useRef } from "react";

import prefersReducedMotion from "motion/prefers-reduced-motion";

// A native <details> has no intermediate state: the panel is laid out at its
// full height the instant `open` flips, so an answer appears and vanishes in a
// single frame. Smoothing that means owning the toggle — opening before the
// panel can grow, and holding the element open until a close has finished
// playing — and animating the panel's height across both edges.
const DURATION = 260;
const EASING = "cubic-bezier(0.33, 0, 0.2, 1)";

// What holds the panel at the last frame it painted is the animation's fill,
// not any style of ours — and the caller cancels a running animation before
// asking for this, which releases that hold. So the panel is already back at
// its natural height by the time the rect is read.
const naturalHeight = (panel) => panel.getBoundingClientRect().height;

// Chrome lays a closed <details> out behind `content-visibility: hidden`, and
// the panel inside goes on reporting the full height it would have if it were
// open. Trusting that on the opening edge animated the panel from its height to
// itself: the answer appeared whole, and only the close ever slid. The rect is
// the truth mid-flight, and while the panel is genuinely open; from a closed
// standstill the height is 0 by definition.
const currentHeight = (panel, opening, running) =>
  !opening || running ? panel.getBoundingClientRect().height : 0;

function FaqItem({ question, answer }) {
  const detailsRef = useRef(null);
  const panelRef = useRef(null);
  const animationRef = useRef(null);
  const closingRef = useRef(false);

  // A slide still playing when the reader navigates away has nothing left to
  // animate: releasing it is tidier than letting it run to completion and
  // finish against an element that is no longer in the document.
  useEffect(
    () => () => {
      if (animationRef.current) animationRef.current.cancel();
    },
    []
  );

  const handleSummaryClick = (event) => {
    const details = detailsRef.current;
    const panel = panelRef.current;

    // Without the Web Animations API (jsdom, older browsers), or for a reader
    // who has asked for less motion, the native toggle is the right answer
    // rather than a half-driven one.
    if (
      !details ||
      !panel ||
      typeof panel.animate !== "function" ||
      prefersReducedMotion()
    ) {
      return;
    }

    // Summary activation by keyboard arrives as a click too, so this covers
    // Enter and Space along with the pointer.
    event.preventDefault();

    const running = animationRef.current;
    // A close holds the element open for the length of its animation, so
    // `details.open` describes a closing panel and an open one identically.
    // While anything is playing, a click means reverse; only at a standstill
    // does the element's own state decide.
    const opening = running ? closingRef.current : !details.open;
    // Read where the panel is right now, mid-flight included, so a toggle that
    // interrupts an animation carries on from what the eye last saw instead of
    // jumping to the end of the previous one.
    const fromHeight = currentHeight(panel, opening, running);

    if (running) running.cancel();
    if (opening) details.open = true;
    // The chevron follows the reader's intent rather than the element's state:
    // `open` stays true for the length of a close, which would hold the arrow
    // pointing up until the panel had already gone.
    details.classList.toggle("is-closing", !opening);
    const toHeight = opening ? naturalHeight(panel) : 0;

    // Height alone. Fading the answer in as well puts the text through a
    // compositing layer for the length of the slide, which switches it to
    // greyscale antialiasing and back — a shimmer, on top of an animation whose
    // whole job is to be unobtrusive.
    const animation = panel.animate(
      { height: [`${fromHeight}px`, `${toHeight}px`] },
      { duration: DURATION, easing: EASING, fill: "forwards" }
    );

    animationRef.current = animation;
    closingRef.current = !opening;
    animation.onfinish = () => {
      animationRef.current = null;
      closingRef.current = false;
      details.classList.remove("is-closing");
      // The fill is what holds the panel at the end of the slide. Flip the
      // element's state first and release the hold second: the other order
      // hands the browser a frame of fully open panel to paint in the middle
      // of a close.
      if (!opening) details.open = false;
      animation.cancel();
    };
  };

  return (
    <details className="service-faq-item" ref={detailsRef}>
      <summary onClick={handleSummaryClick}>
        <span>{question}</span>
        <svg
          className="service-faq-chevron"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M3 5.5L7 9.5l4-4"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </summary>
      <div className="service-faq-body" ref={panelRef}>
        <p>{answer}</p>
      </div>
    </details>
  );
}

export default FaqItem;
